import React, { useMemo, useRef, useState } from 'react';
import {
  AssemblyBackflowTest,
  BackflowAssembly,
  BackflowAssemblyVersion,
  NoticeSent,
} from './types';
import { Flex } from 'rebass';
import {
  convertAssemblyVersion,
  convertBackflowTest,
  convertNoticeSent,
} from './helpers';
import { sortHistoryItems } from '../Timeline/helpers';
import { HistoryItem } from '../Timeline/types';
import Timeline from '../Timeline/Timeline';
import useMode from '../../hooks/useMode';

type AssemblyHistoryProps = {
  replacedBy?: BackflowAssembly['replacedByAssembly'];
  noticesSent: NoticeSent[];
  backflowTests: AssemblyBackflowTest[];
  versions: BackflowAssemblyVersion[];
  refetch?: () => Promise<unknown>;
};

export type HistoryItemType = 'VERSION' | 'NOTICE_SENT' | 'BACKFLOW_TEST';

export type AssemblyHistoryItem = HistoryItem<HistoryItemType>;

// History state
export const assemblyHistoryItemTypes: HistoryItemType[] = [
  'VERSION',
  'NOTICE_SENT',
  'BACKFLOW_TEST',
];

const AssemblyHistory: React.FC<AssemblyHistoryProps> = ({
  replacedBy,
  noticesSent,
  backflowTests,
  versions,
  refetch,
}) => {
  const mode = useMode();

  const scrollDiv = useRef<any>();

  const matchVisibleHistoryTypes = (item: AssemblyHistoryItem) => {
    if (!selectedHistoryItemTypes) {
      return true;
    }
    return selectedHistoryItemTypes.includes(item.type);
  };

  const [selectedHistoryItemTypes, setSelectedHistoryItemTypes] = useState<
    HistoryItemType[]
  >(assemblyHistoryItemTypes);

  const amendedHistoryItems = useMemo<AssemblyHistoryItem[]>(() => {
    let previousVersion: BackflowAssemblyVersion | undefined;
    const items: AssemblyHistoryItem[] = [
      ...(replacedBy && replacedBy.session?.userProfile
        ? [
            {
              id: replacedBy.id,
              created: replacedBy.created,
              profile: replacedBy.session?.userProfile,
              type: 'VERSION',
              icon: 'edit',
              url: `/${mode}/assemblies/${replacedBy.id}`,
              title: 'Updated / Replaced',
            } as AssemblyHistoryItem,
          ]
        : []),
      ...noticesSent.map(convertNoticeSent),
      ...backflowTests.map((test) => {
        return convertBackflowTest(test, mode);
      }),
      ...versions
        .map((version) => {
          const converted = convertAssemblyVersion(
            version,
            previousVersion,
            mode
          );
          previousVersion = version;
          return converted;
        })
        .slice(1),
    ];

    // Convert, concat, filter, sort and group
    return items.filter(matchVisibleHistoryTypes).sort(sortHistoryItems);
  }, [
    noticesSent,
    backflowTests,
    versions,
    selectedHistoryItemTypes,
    matchVisibleHistoryTypes,
  ]);

  const availableHistoryItemTypes = useMemo(() => {
    const available: HistoryItemType[] = [];

    if (noticesSent.length) {
      available.push('NOTICE_SENT');
    }
    if (backflowTests.length) {
      available.push('BACKFLOW_TEST');
    }
    if (versions.length > 1) {
      available.push('VERSION');
    }
    return available;
  }, [noticesSent, backflowTests, versions]);

  return (
    <Flex flexDirection="column" height="100%" flexGrow={1} flexShrink={1}>
      <Timeline
        selectedHistoryItemTypes={selectedHistoryItemTypes}
        setSelectedHistoryItemTypes={setSelectedHistoryItemTypes}
        historyItemTypes={availableHistoryItemTypes}
        historyItems={amendedHistoryItems}
        scrollDivRef={scrollDiv}
      />
    </Flex>
  );
};

export default AssemblyHistory;
